import {
    Ad,
    AdBadge,
    AdContent,
    AdContentTitle,
    AdImage,
    AdImageFrame,
    AdImageFrameOverlay,
    AdLink,
    AdTools,
} from '@finn-no/troika-react-ads';
import { SquareAutoImageFormat } from '@finn-no/troika-react-auto-image-format';
import React, { useRef, useState, useEffect } from 'react';
import { useTrackInscreen } from './hooks';
import Tracking from './tracking';
import classNames from 'classnames';

type AdProps = {
    ad: import('./types').Recommendation;
    trackAdClick: (adId: string, trackingUrlClick: string[]) => void;
    imagesCdnUrl: string;
    logo: string;
    tracking: Tracking;
    useOverflow: boolean;
    url?: string;
} & Pick<
    React.ComponentProps<
        typeof import('@finn-no/troika-react-ad-preview').default
    >,
    'rightActions'
>;
const JobAd = (props: AdProps) => {
    const {
        trackAdClick,
        ad,
        tracking,
        rightActions,
        useOverflow,
        imagesCdnUrl,
    } = props;

    const [badgeIsAbsolute, setBadgeAbsolute] = useState(false);
    const inscreenTrackingUrls = Tracking.getInscreenUrl(ad);
    const trackingUrlClick = Tracking.getClickUrl(ad);
    const [titleSpace, setTitleSpace] = useState(16);

    const domRef = useRef<HTMLDivElement>(null);
    const adBadgeRef = useRef<HTMLDivElement>(null);
    const heartRef = useRef<HTMLDivElement>(null);
    const companyRef = useRef<HTMLDivElement>(null);

    useTrackInscreen({
        adId: ad.itemId,
        tracking,
        ref: domRef,
        inscreenTrackingUrls,
        pulseContext: ad?.tracking?.pulse,
    });

    useEffect(() => {
        if (
            !adBadgeRef.current ||
            !domRef.current ||
            !heartRef.current ||
            !companyRef
        )
            return;
        const itemW = domRef.current.offsetWidth - 10;
        const badgeWidth = (adBadgeRef.current.offsetWidth / itemW) * 100;
        const heartWidth = (heartRef.current.offsetWidth / itemW) * 100;
        setBadgeAbsolute(badgeWidth + heartWidth < 85);
    }, [adBadgeRef, domRef, heartRef]);

    useEffect(() => {
        // Set height padding
        if (companyRef.current.offsetHeight > 31) {
            setTitleSpace(42);
        }
        if (
            companyRef.current.offsetHeight > 15 &&
            companyRef.current.offsetHeight < 32
        )
            setTitleSpace(28);
    }, []);

    const styles = {
        paddingBottom: `${titleSpace}px`,
    };

    return (
        <Ad
            key={ad.itemId}
            ref={domRef}
            onClick={(e) => {
                trackAdClick(ad.itemId, trackingUrlClick);
                tracking.trackClickWithPulse(ad.tracking.pulse);
            }}
            data-rec-item
            className={classNames('u-border-radius-8px', {
                'overflowmenu-active': useOverflow,
            })}
        >
            <div className="job-wrapper">
                <AdImage>
                    <AdImageFrame>
                        <SquareAutoImageFormat
                            cdnUrl={imagesCdnUrl}
                            url={ad.image?.url}
                            alt=""
                            sizes="(min-width: 990px) 330px, (min-width: 768px) 33vw, (min-width: 480px) 50vw"
                        />
                        {ad.label && ad.image?.type !== 'LOGO' && (
                            <AdImageFrameOverlay>
                                {ad.label}
                            </AdImageFrameOverlay>
                        )}
                    </AdImageFrame>
                </AdImage>
                <AdContent className="u-pa8 u-bg-marble">
                    <AdContentTitle lineClamp2>
                        <AdLink href={ad.url} title={ad.heading} style={styles}>
                            {ad.heading}
                        </AdLink>
                    </AdContentTitle>
                    {ad.overlay?.p4p?.label && (
                        <AdBadge
                            ref={adBadgeRef}
                            className={
                                badgeIsAbsolute
                                    ? 'recommendation-badge-absolute'
                                    : 'recommendation-badge-static'
                            }
                        >
                            {ad.overlay.p4p.label}
                        </AdBadge>
                    )}

                    {ad.overlay?.easyapply?.label && (
                        <AdBadge
                            ref={adBadgeRef}
                            className={classNames(
                                'status-is-easyapply',
                                badgeIsAbsolute
                                    ? 'recommendation-badge-absolute'
                                    : 'recommendation-badge-static'
                            )}
                        >
                            {ad.overlay.easyapply.label}
                        </AdBadge>
                    )}
                    <div className="recommendation-job-subcontent u-position-absolute u-d1">
                        <div className="u-float-left" ref={companyRef}>
                            {ad.content?.company_name}
                        </div>
                        <div className="u-float-right u-text-right">
                            {ad.location?.combined}
                            {ad.content?.published_relative &&
                                ` ${String.fromCharCode(8226)} ${
                                    ad.content.published_relative
                                }`}
                        </div>
                    </div>
                </AdContent>
                {rightActions && (
                    <AdTools ref={heartRef}>{rightActions}</AdTools>
                )}
            </div>
        </Ad>
    );
};

export default JobAd;
